// features/feedback/api.ts — the staff app's "Submit feedback" calls
// (backend/routes/staff-feedback.js). Multipart, so it goes through
// authFetch rather than lib/api/client.ts's JSON client.
import { useQuery } from '@tanstack/react-query';
import { authFetch } from '@/api';
import { keys } from '@/lib/queryKeys';

export type FeedbackCategory = 'bug' | 'idea' | 'question' | 'praise' | 'other';
export type FeedbackStatus = 'new' | 'reviewing' | 'converted' | 'resolved' | 'dismissed';

export interface MyFeedback {
  id: number;
  ref: string;
  category: FeedbackCategory;
  title: string | null;
  message: string | null;
  status: FeedbackStatus;
  reply_to_user: string | null;
  module: string | null;
  audio_parts: number;
  created_at: string;
  updated_at: string;
}

export interface FeedbackSubmission {
  category: FeedbackCategory;
  title?: string;
  message?: string;
  transcript?: string;
  module?: string;
  audio?: Blob[];
  audioSeconds?: number;
  attachment?: File | null;
}

export async function submitFeedback(s: FeedbackSubmission): Promise<{ id: number; ref: string }> {
  const fd = new FormData();
  fd.append('category', s.category);
  if (s.title) fd.append('title', s.title);
  if (s.message) fd.append('message', s.message);
  if (s.transcript) fd.append('transcript', s.transcript);
  if (s.module) fd.append('module', s.module);
  fd.append('page_url', window.location.pathname + window.location.search);
  fd.append('user_agent', navigator.userAgent);
  fd.append('screen', `${window.innerWidth}x${window.innerHeight}`);
  (s.audio ?? []).forEach((blob, i) => fd.append('audio', blob, `voice-${i + 1}.webm`));
  if (s.audio?.length && s.audioSeconds !== undefined) fd.append('audio_seconds', String(s.audioSeconds));
  if (s.attachment) fd.append('attachment', s.attachment, s.attachment.name || 'screenshot.png');

  const res = await authFetch('/api/me/feedback', { method: 'POST', body: fd });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Could not send feedback (${res.status})`);
  return data;
}

export async function fetchMyFeedback(): Promise<MyFeedback[]> {
  const res = await authFetch('/api/me/feedback/mine');
  const data = await res.json().catch(() => []);
  if (!res.ok) throw new Error((data as { error?: string }).error || 'Could not load your feedback');
  return data;
}

export function useMyFeedback(enabled: boolean) {
  return useQuery({ queryKey: keys.feedback.mine(), queryFn: fetchMyFeedback, enabled, staleTime: 30_000 });
}

/** Text to start the form with — the user can still edit or delete it before sending. */
export interface FeedbackPrefill { category?: FeedbackCategory; message?: string }

/** Opens the feedback panel from anywhere (the profile menu, the Copilot's "Send this to the ClintoBytes team"). */
export const OPEN_FEEDBACK_EVENT = 'hms:open-feedback';
export function openFeedback(prefill?: FeedbackPrefill) {
  window.dispatchEvent(new CustomEvent(OPEN_FEEDBACK_EVENT, { detail: prefill ?? null }));
}
